import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { en } from './locales/en';
import { pt } from './locales/pt';

/**
 * App-wide pt-PT / en bilingual layer — no dependency, in the spirit of the
 * other `lib/` hooks. A nested dictionary is addressed by dotted keys
 * ("nav.painel") with `{var}` interpolation; the chosen language is kept in
 * localStorage and mirrored onto `<html lang>`. A missing key falls back to
 * Portuguese, then to the key itself, so a half-translated screen never shows
 * a blank. Portuguese is the source of truth (`pt`); English must mirror its
 * shape (enforced by `AppDictionary`).
 */

export type Lang = 'pt' | 'en';
export const LANGS: readonly Lang[] = ['pt', 'en'];

const STORAGE_KEY = 'ciimar-lang';
const DEFAULT_LANG: Lang = 'pt';

/** Nested dictionary: string leaves addressed by dotted keys. */
export type Dictionary = { [key: string]: string | Dictionary };

const DICTS: Record<Lang, Dictionary> = { pt, en };

function readStored(): Lang {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'pt' || v === 'en') return v;
  } catch {
    /* storage may be unavailable */
  }
  return DEFAULT_LANG;
}

function lookup(dict: Dictionary, key: string): string | undefined {
  let cur: string | Dictionary | undefined = dict;
  for (const part of key.split('.')) {
    if (cur && typeof cur === 'object' && part in cur) cur = cur[part];
    else return undefined;
  }
  return typeof cur === 'string' ? cur : undefined;
}

/** Replace {name} placeholders from `vars`; unknown ones are left untouched. */
function interpolate(text: string, vars?: Record<string, string | number>): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

export type Translate = (key: string, vars?: Record<string, string | number>) => string;

interface I18n {
  lang: Lang;
  setLang: (lang: Lang) => void;
  toggle: () => void;
  t: Translate;
}

const I18nContext = createContext<I18n | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Lang>(readStored);
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* ignore */
    }
    document.documentElement.lang = lang;
  }, [lang]);
  const t = useCallback<Translate>(
    (key, vars) => interpolate(lookup(DICTS[lang], key) ?? lookup(DICTS.pt, key) ?? key, vars),
    [lang],
  );
  const value = useMemo<I18n>(
    () => ({
      lang,
      setLang,
      toggle: () => setLang((l) => (l === 'pt' ? 'en' : 'pt')),
      t,
    }),
    [lang, t],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>');
  return ctx;
}

/** Shorthand for components that only need the translate function. */
export function useT(): Translate {
  return useI18n().t;
}

/** A record carrying a Portuguese label and an optional English one. */
export interface Localizable {
  label?: string | null;
  labelEn?: string | null;
}

/** Pick the English value in `en` mode when present, else the Portuguese one. */
export function pickText(
  pt: string | null | undefined,
  en: string | null | undefined,
  lang: Lang,
): string {
  if (lang === 'en' && en && en.trim()) return en;
  return pt ?? '';
}

/** Resolve a {label, labelEn} record for the given language. */
export function pickLabel(obj: Localizable | null | undefined, lang: Lang): string {
  return pickText(obj?.label, obj?.labelEn, lang);
}

/**
 * Hook returning a resolver for admin-defined labels: in `en` it uses `labelEn`
 * when set, otherwise falls back to the Portuguese `label`. Use for field,
 * section, option and dashboard-card labels (data the admin edits), never for
 * the static UI copy handled by `t()`.
 */
export function useLocalizedLabel(): (obj: Localizable | null | undefined) => string {
  const { lang } = useI18n();
  return (obj) => pickLabel(obj, lang);
}
